<template>
  <div class="data-table table-wrapper">
    <header class="table-header">
      <div class="row justify-content-between align-items-center mb-2">
        <div class="table-title d-flex align-items-center col">
          <div class="img-header" v-if="tableIcon">
            <img :src="require(`@/assets/img/${tableIcon}`)" alt="Airplane icon" class="table-icon" />
          </div>
          <h1 class="table-title text-left mb-0">{{ titleCase(tableName.replace('-', ' ')) }}</h1>
        </div>

        <div class="btn-container col" v-if="exportUrl">
          <b-button variant="success" size="sm" class="d-block ml-auto" @click="openExportModal()" :disabled="exportLoading || isTableLoading">
            <div v-if="exportLoading">
              <b-spinner small></b-spinner>
              <span class="ml-2">Exporting...</span>
            </div>
            <span v-else>Export</span>
          </b-button>
        </div>
      </div>

      <div class="table-helpers row justify-content-between align-items-center">
        <b-form-group
          class="table-page-size align-items-center col-12 col-md-4 col-lg-3 col-xl-3 mb-2 mb-md-0"
          label="Items per page"
          label-for="select-table-page-size"
          label-cols="6"
          label-cols-xl="5"
          label-class="table-page-size-label col-form-label-sm"
        >
          <b-form-select id="select-table-page-size" class="col pr-0 mr-0 float-right float-md-left" size="sm" v-model="pageSize" :options="pageSizeOpts"></b-form-select>
        </b-form-group>

        <b-form-group
          class="table-search align-items-center text-left text-md-right col-12 col-md-6 col-lg-6 mb-0"
          label-cols="3"
          label="Search"
          label-for="input-table-search"
          label-class="table-search-label col-form-label-sm"
        >
          <b-input-group>
            <b-form-input id="input-table-search" type="text" placeholder="Search on any field..." v-model="filter" debounce="500" trim></b-form-input>
            <b-input-group-append v-if="selectedFilterWindow">
              <b-button variant="outline-dark" size="sm" :pressed="isFilterWindowOpen" @click="toggleFiltersWindow()">Filters</b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </div>
    </header>

    <!-- Additional filters modal -->
    <component :is="selectedFilterWindow" v-model="isFilterWindowOpen" @filtersSetup="additionalFilters = $event" @filtersSubmitted="handleAdditionalFilters"></component>

    <!-- Export modal -->
    <b-modal id="export-modal" ref="exportModal" title="Export Results" hide-footer>
      <b-form @submit.stop.prevent="exportResults()">
        <div class="container-fluid">
          <b-form-group label="File Type" label-cols="3" label-for="input-file-type">
            <b-form-select class="col-4" id="input-file-type" v-model="exportForm.fileType" :options="exportForm.fileTypeOpts"></b-form-select>
          </b-form-group>
          <b-form-group label="File Name" label-cols="3" label-for="input-file-name">
            <b-form-input id="input-file-name" v-model="exportForm.fileName" :state="validateFileName ? null : false" required />
            <b-form-invalid-feedback :state="validateFileName" v-if="exportForm.fileName.length === 0">
              Must enter a valid file name
            </b-form-invalid-feedback>
            <b-form-invalid-feedback :state="validateFileName" v-if="exportForm.fileName.length !== 0">
              File name cannot include these characters: / \ | " &lt; &gt; ? *
            </b-form-invalid-feedback>
          </b-form-group>
          <b-form-group label="Worksheet Name" label-cols="3" label-for="input-worksheet-name" :state="validateSheetName" v-if="exportForm.fileType === 'xlsx'">
            <b-form-input id="input-worksheet-name" v-model="exportForm.sheetName" :state="validateSheetName ? null : false" required />
            <b-form-invalid-feedback :state="validateSheetName">Must enter a valid worksheet name</b-form-invalid-feedback>
          </b-form-group>

          <div class="btn-container">
            <b-button type="submit" variant="success" class="float-right" :disabled="!allowExport">Save</b-button>
            <b-button variant="danger" class="float-right mr-2" @click="$bvModal.hide('export-modal')">Cancel</b-button>
          </div>
        </div>
      </b-form>
    </b-modal>

    <b-alert variant="danger" :show="errors.length > 0" class="mt-3 mb-2">{{ errors[0] }}</b-alert>

    <b-table
      responsive
      sticky-header
      bordered
      show-empty
      ref="dataTable"
      class="table-container"
      :id="tableId"
      :items="myProvider"
      :fields="columns"
      :busy.sync="isTableLoading"
      :currentPage="pageIndex"
      :perPage="pageSize"
      :filter="filter"
      :sortBy.sync="sortBy"
      :sortDesc.sync="sortDesc"
      @refreshed.once="isTableInitialized = true"
    >
      <template v-slot:head()="data">
        <div :class="alignField(data.field)">
          {{ data.field.alias ? data.field.alias : titleCase(data.label) }}
        </div>
      </template>

      <template v-slot:cell(action)="data" v-if="action">
        <b-button variant="primary" size="sm" class="d-block mx-auto" @click="viewRecord(data.item)">
          {{ action.name }}
        </b-button>
      </template>

      <template v-slot:cell()="data">
        <div :class="alignField(data.field)">
          {{ normalizeValue(data.value, data.field) }}
        </div>
      </template>

      <template v-slot:table-busy>
        <div class="table-overlay text-center text-danger">
          <b-spinner class="table-spinner"></b-spinner>
        </div>
      </template>

      <template v-slot:empty>
        <h5 class="text-center">No records to display.</h5>
      </template>

      <template v-slot:emptyfiltered>
        <h5 class="text-center">There are no records matching your search.</h5>
      </template>
    </b-table>

    <footer class="table-footer">
      <div class="table-pagination row justify-content-between align-items-center">
        <span class="table-pagination-range col">
          {{ currentPageRange() }}
        </span>

        <div class="col">
          <b-pagination
            v-if="isTableInitialized"
            class="table-pagination-control justify-content-end"
            v-model="pageIndex"
            :total-rows="totalRecords"
            :per-page="pageSize"
            :aria-controls="tableId"
            :aria-label="tableName + ' pagination'"
          ></b-pagination>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import moment from 'moment';
import FilterFlights from '../components/Filter/FilterFlights';
import FilterFlightTravelers from '../components/Filter/FilterFlightTravelers';
import FilterVoyages from '../components/Filter/FilterVoyages';
import FilterVoyageTravelers from '../components/Filter/FilterVoyageTravelers';

export default {
  name: 'AppDataTable',

  props: {
    tableName: { type: String, required: true },
    tableIcon: String, // name of table icon
    dataProviderFunc: Function, // provider for table data,
    exportUrl: String, // API request path for table data
    action: Object
  },

  components: {
    FilterFlights,
    FilterVoyages,
    FilterFlightTravelers,
    FilterVoyageTravelers
  },

  data() {
    const routeQueries = this.$router.currentRoute.query;

    return {
      isTableInitialized: false, // Needed for pagination to initialize correctly
      isTableLoading: false,
      isFilterWindowOpen: false,
      pageIndex: parseInt(routeQueries['pgIdx']) || 1,
      pageSize: parseInt(routeQueries['pgSz']) || 10,
      pageSizeOpts: [
        { value: 10, text: '10' },
        { value: 25, text: '25' },
        { value: 50, text: '50' },
        { value: 100, text: '100' }
      ],
      filter: routeQueries['filter'] || '',
      additionalFilters: null,
      sortBy: '',
      sortDesc: false,
      totalRecords: 0,
      fields: [],
      errors: [],
      exportForm: {
        fileTypeOpts: [
          { value: 'csv', text: 'CSV' },
          { value: 'xlsx', text: 'Excel (xlsx)' }
        ],
        fileType: 'csv',
        fileName: '',
        sheetName: this.titleCase(this.tableName + ' results')
      },
      exportLoading: false
    };
  },

  computed: {
    selectedFilterWindow() {
      if (this.tableName.includes('flights')) return FilterFlights;
      if (this.tableName.includes('voyages')) return FilterVoyages;
      if (this.tableName.includes('flight-travelers')) return FilterFlightTravelers;
      if (this.tableName.includes('voyage-travelers')) return FilterVoyageTravelers;
      return null;
    },

    tableId() {
      return `table-${this.tableName}`;
    },

    /** Columns to show in table. */
    columns() {
      return this.fields.filter((val) => {
        return val.key !== 'AIRCRAFT_ID' && val.key !== 'VESSEL_ID' && val.key !== 'TRAVELER_NO';
      });
    },

    /** Returns the name of the primary key for the loaded data. */
    primaryKey() {
      if (this.tableName.includes('flights')) return 'AIRCRAFT_ID';
      if (this.tableName.includes('voyages')) return 'VESSEL_ID';
      if (this.tableName.includes('travelers')) return 'TRAVELER_NO';
      return '';
    },

    /** Returns true if the input for the file name meets the following:
     * + fileName.length > 0
     * + fileName excludes the characters : / \ \ | " < > ? *
     */
    validateFileName() {
      return this.exportForm.fileName.length > 0 && /^((?![:/\\|"<>?*]+).)*$/g.test(this.exportForm.fileName);
    },

    /** Returns true if the input for the sheet name meets the following:
     * + sheetName.length > 0
     */
    validateSheetName() {
      return this.exportForm.sheetName.length > 0;
    },

    /** Returns true if the export form data is valid. */
    allowExport() {
      return this.validateFileName && (this.exportForm.fileType === 'xlsx' ? this.validateSheetName : true);
    }
  },

  methods: {
    /** Align field based on data type and/or name. */
    alignField(field) {
      return {
        'text-right': field.type === 'date' || field.type === 'time' || field.type === 'date-time',
        'text-center': field.key.toLowerCase() === 'action'
      };
    },

    /** Display range of data currently being shown. */
    currentPageRange() {
      if (this.totalRecords === 0) {
        return 'No records were found';
      }
      let firstRecord = (this.pageIndex - 1) * this.pageSize + 1;
      let lastRecord = this.totalRecords < firstRecord + this.pageSize - 1 ? this.totalRecords : firstRecord + this.pageSize - 1;

      return `Showing ${firstRecord.toLocaleString('en')} - ${lastRecord.toLocaleString('en')} of ${this.totalRecords.toLocaleString('en')} items`;
    },

    titleCase(value) {
      return value.replace(/_/g, ' ').replace(/\w\S*/g, function(txt) {
        if (/(id|dob)/gi.test(txt)) return txt.toUpperCase();
        if (/dt/gi.test(txt)) return 'Date';
        if (/num/gi.test(txt)) return '#';
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
      });
    },

    normalizeValue(value, col) {
      if (!value) return 'N/A';

      if (col.type === 'date') {
        if (col.key.toLowerCase().includes('birth')) {
          return moment(value).format('YYYY-MMM-DD');
        }
        return moment(value, 'HH:mm:ss YYYY-MM-DD').format('h:mm A, YYYY-MMM-DD');
      }
      if (col.key.toLowerCase().includes('gender')) {
        if (value.toLowerCase() === 'f') return 'Female';
        if (value.toLowerCase() === 'm') return 'Male';
      }

      return value;
    },

    toggleFiltersWindow() {
      this.isFilterWindowOpen = !this.isFilterWindowOpen;
    },

    handleAdditionalFilters(filters) {
      this.additionalFilters = filters;

      // Hide filters window
      this.toggleFiltersWindow();

      // Trigger table refresh
      this.$refs.dataTable.refresh();
    },

    /** Fetch data for table from API resource.
     *  @param tblCtx Table context
     */
    async myProvider(tblCtx) {
      this.errors = []; // Reset errors

      // Generate query parameters
      let queryParams = {};
      queryParams.pgIdx = tblCtx.currentPage;
      queryParams.pgSz = tblCtx.perPage;
      if (tblCtx.filter) queryParams.filter = tblCtx.filter;
      if (tblCtx.sortBy) {
        const sort = (tblCtx.sortDesc ? '-' : '+') + tblCtx.sortBy;
        queryParams.srt = sort;
      }
      if (this.additionalFilters) queryParams = { ...queryParams, ...this.additionalFilters };

      let items = [];
      try {
        // Get records
        const data = await this.dataProviderFunc(queryParams);

        this.totalRecords = data.rowCount;
        this.fields = data.tableColumns;
        if (this.action) {
          this.fields.push('action');
        }

        items = data.payload;
      } catch (err) {
        if (!err.response) {
          this.errors.push('Error: A network error occurred.');
        } else {
          if (Array.isArray(err.response.data.message)) {
            this.errors = err.response.data.message;
          } else {
            this.errors.push(err.response.data.message || 'Something went wrong while trying to get records.');
          }
        }
        // console.log(err);
      }
      return items;
      // return [] || items;
    },

    /** Go to selected record. */
    async viewRecord(selectedRecord) {
      const id = selectedRecord[this.primaryKey];
      const actionUrl = this.action.url.replace(/\/:\w*\/?/g, `/${id}/`);
      try {
        // Go to record info page
        this.$router.push(actionUrl);
      } catch (err) {
        if (!err.response) {
          this.errors.push('Error: A network error occurred.');
        } else {
          if (Array.isArray(err.response.data.message)) {
            this.errors = err.response.data.message;
          } else {
            this.errors.push(err.response.data.message || 'Something went wrong while trying to view the record.');
          }
        }
      }
    },

    openExportModal() {
      // Set default file name in case none provided
      if (!this.exportForm.fileName) {
        // TableName_Results_CurrentDateTime
        this.exportForm.fileName = `${this.titleCase(this.tableName)}_Results_${new Date().toISOString().replace(/[:.]/g, '_')}`;
      }
      this.$bvModal.show('export-modal');
    },

    /** Export results to CSV or XLSX format */
    async exportResults() {
      //   Make sure that export form data is valid
      if (!this.allowExport) {
        return this.errors.push('Invalid input for file export');
      }

      this.$bvModal.hide('export-modal'); // Close the export modal
      this.exportLoading = true;
      this.errors = []; // Reset errors

      // Generate query parameters
      let queryParams = {};
      if (this.filter) queryParams.filter = this.filter;
      if (this.sortBy) {
        const sort = (this.sortDesc ? '-' : '+') + this.sortBy;
        queryParams.srt = sort;
      }
      if (this.additionalFilters) queryParams = { ...queryParams, ...this.additionalFilters };
      queryParams.fileType = this.exportForm.fileType;
      queryParams.fileName = this.exportForm.fileName;
      if (this.exportForm.fileType === 'xlsx') queryParams.sheetName = this.exportForm.sheetName;

      try {
        // Get download token
        const response = await this.$http({ url: '/auth/download-token', method: 'GET' });

        // Add token to query params
        queryParams.token = response.data.downloadToken;

        // Generate download URL
        const downloadUrl = process.env.VUE_APP_API_URL + this.$http.getUri({ url: this.exportUrl, method: 'GET', responseType: 'arraybuffer', params: queryParams });

        // Create browser link for file download
        const fileLink = document.createElement('a');
        fileLink.href = downloadUrl;
        fileLink.setAttribute('download', null);
        fileLink.setAttribute('type', this.exportForm.fileType === 'csv' ? 'text/csv' : 'application/vnd.ms.excel');
        document.body.appendChild(fileLink);

        // Click link to start download
        fileLink.click();
        this.exportLoading = false;

        // Remove link from page
        document.body.removeChild(fileLink);
      } catch (err) {
        this.exportLoading = false;

        if (!err.response) {
          this.errors.push('Error: A network error occurred.');
        } else {
          if (Array.isArray(err.response.data.message)) {
            this.errors = err.response.data.message;
          } else {
            this.errors.push(err.response.data.message || 'Something went wrong while trying to export the results.');
          }
        }
        // console.log(err);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../assets/styles/custom.scss';

.table-container ::v-deep.table.b-table[aria-busy='true'] {
  opacity: 1 !important;
}

.table-container ::v-deep.table.b-table[aria-busy='true'] .table-overlay {
  position: absolute;
  background-color: rgba(0, 0, 0, 0.75);
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 10; /* make sure spinner is over table */
}

.table-container ::v-deep.table.b-table[aria-busy='true'] tbody tr {
  height: 400px;
}

.table-container ::v-deep.table.b-table[aria-busy='true'] .table-overlay .table-spinner {
  color: var(--primary);
  position: absolute;
  left: calc(50% - 50px);
  top: calc(50% - 50px);
  height: 100px;
  width: 100px;
  border-width: 10px;
}

.table-container ::v-deep.table.b-table thead th {
  background-color: #4a5864 !important;
  color: white !important;
  letter-spacing: 0.6px;
  font-size: 0.9rem;
  font-weight: 700;
}

.table-container ::v-deep.table.b-table:not([aria-busy='true']) tbody tr:not(.b-table-empty-row) {
  transition: background 0.1s ease-in-out;
}
.table-container ::v-deep.table.b-table:not([aria-busy='true']) tbody tr:not(.b-table-empty-row):hover {
  box-shadow: 0px 2px 4px 0px rgba(32, 32, 32, 0.5);
  background-color: #7acaff;
}

.table-container ::v-deep.table.b-table:not([aria-busy='true']) tbody td {
  font-size: 14px;
}

@media (min-width: 576px) {
  .modal-dialog {
    max-width: 600px !important;
  }
}
</style>
